import { useState } from 'react';
import './LoginPage.css';
import { API_BASE } from './config';

const translations = {
  en: {
    pageTitle: 'Welcome Back',
    pageSubtitle: 'Sign in to your account',
    emailLabel: 'Email Address',
    emailPlaceholder: 'Enter your email address',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    loginButton: 'Sign In',
    loggingInButton: 'Signing In...',
    registerLink: "Don't have an account? Create one",
    backToHome: 'Back to Homepage',
    errorTitle: 'Login Failed',
    invalidCredentials: 'Invalid email or password',
    requiredFields: 'Please fill in all fields',
    invalidEmail: 'Please enter a valid email address',
  },
  id: {
    pageTitle: 'Selamat Datang Kembali',
    pageSubtitle: 'Masuk ke akun Anda',
    emailLabel: 'Alamat Email',
    emailPlaceholder: 'Masukkan alamat email Anda',
    passwordLabel: 'Kata Sandi',
    passwordPlaceholder: 'Masukkan kata sandi Anda',
    loginButton: 'Masuk',
    loggingInButton: 'Sedang Masuk...',
    registerLink: 'Belum punya akun? Buat akun',
    backToHome: 'Kembali ke Beranda',
    errorTitle: 'Login Gagal',
    invalidCredentials: 'Email atau kata sandi salah',
    requiredFields: 'Silakan isi semua field',
    invalidEmail: 'Silakan masukkan alamat email yang valid',
  },
};

function LoginPage({ language = 'en', onNavigate }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const copy = translations[language];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') setEmail(value);
    if (name === 'password') setPassword(value);
    if (error) setError('');
  };

  // Helper: redirect based on roles array
  // Role codes in DB: super_admin, admin, website_admin, approver, admissions, editor
  const redirectByRole = (roles, user) => {
    const r = roles.map(x => String(x).toLowerCase().trim());
    console.log('[LoginPage] Redirecting - user:', user.full_name, 'roles:', r);
    // Store user info in session for dashboard components
    sessionStorage.setItem('auth_user', JSON.stringify({
      name: user.full_name,
      email: user.email,
      roles: r,
    }));

    if (r.includes('super_admin')) {
      window.location.href = '/admin-dashboard';
    } else if (r.includes('website_admin') || r.includes('admin')) {
      // 'admin' is the legacy role for website admin
      window.location.href = '/website-admin-cms';
    } else if (r.includes('approver') || r.includes('principal')) {
      window.location.href = '/principal-dashboard';
    } else if (r.includes('admissions')) {
      window.location.href = '/admissions-portal';
    } else if (r.includes('editor')) {
      // Editor also goes to website CMS
      window.location.href = '/website-admin-cms';
    } else {
      // Unknown role — go to admissions portal as safe default
      window.location.href = '/admissions-portal';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) { setError(copy.requiredFields); return; }
    if (!validateEmail(email)) { setError(copy.invalidEmail); return; }

    setIsSubmitting(true);

    try {
      // Staff login via routed endpoint
      console.log('[LoginPage] Using API_BASE:', API_BASE);
      console.log('[LoginPage] Attempting staff login with email:', email);
      
      let response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // CRITICAL: Include cookies for session
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      let data = await response.json();
      console.log('[LoginPage] Staff login response status:', response.status);
      console.log('[LoginPage] Staff login response data:', data);

      // Check if staff login succeeded
      if (data.ok && data.data?.user && Array.isArray(data.data.user.roles) && data.data.user.roles.length > 0) {
        console.log('[LoginPage] Staff login successful, roles:', data.data.user.roles);
        redirectByRole(data.data.user.roles || [], data.data.user);
        return;
      }

      // If staff login failed, try parent/admissions login
      if (!data.ok) {
        console.log('[LoginPage] Staff login failed, trying admissions login...');
        
        try {
          response = await fetch(`${API_BASE}/admissions/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email: email.toLowerCase(), password }),
          });

          const admissionsData = await response.json();
          console.log('[LoginPage] Admissions login response:', admissionsData);

          if (admissionsData.ok && admissionsData.data) {
            console.log('[LoginPage] Admissions login successful');
            window.location.href = '/admissions-portal';
            return;
          }

          // Both failed - show the error from staff login (more relevant)
          setError(data.message || admissionsData.message || copy.invalidCredentials);
          return;
        } catch (admissionsErr) {
          console.error('[LoginPage] Admissions login error:', admissionsErr);
          setError(data.message || copy.invalidCredentials);
          return;
        }
      }

      // Unexpected response format
      setError(copy.invalidCredentials);

    } catch (err) {
      console.error('[LoginPage] Network error:', err);
      setError('Service unavailable: ' + (err.message || 'Please check your connection'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  const handleRegister = () => {
    if (onNavigate) {
      onNavigate('register');
    } else {
      window.location.hash = '#register';
      window.location.reload();
    }
  };

  return (
    <div className="login-page">
      <button type="button" className="back-button-fixed" onClick={handleBackToHome}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5"/>
          <path d="M12 19l-7-7 7-7"/>
        </svg>
        <span>{copy.backToHome}</span>
      </button>

      {error && (
        <div className="error-toast-modern">
          <div className="error-toast-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div className="error-toast-text">
            <strong>{copy.errorTitle}</strong>
            <p>{error}</p>
          </div>
          <button type="button" className="error-toast-close" onClick={() => setError('')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <div className="login-container">
        <div className="login-header">
          <div className="login-brand">
            <img src="/logo_phs.png" alt="PHS Logo" className="login-logo" />
            <div className="login-brand-text">
              <h1>{copy.pageTitle}</h1>
              <p>{copy.pageSubtitle}</p>
            </div>
          </div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="email">{copy.emailLabel} *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={email}
                onChange={handleInputChange}
                placeholder={copy.emailPlaceholder}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{copy.passwordLabel} *</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="form-input"
                  value={password}
                  onChange={handleInputChange}
                  placeholder={copy.passwordPlaceholder}
                  required
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            className="login-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="spinner" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3"/>
                </svg>
                {copy.loggingInButton}
              </>
            ) : (
              copy.loginButton
            )}
          </button>

          <div className="login-footer">
            <button type="button" className="register-link" onClick={handleRegister}>
              {copy.registerLink}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
